import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Wallet, User, Coins } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReportDialog, TrustBadge } from "@/components/ReportDialog";
import { useI18n } from "@/i18n";
import { useAuth } from "@/AuthContext";
import api, { apiError } from "@/api";

export default function JobDetail() {
  const { id } = useParams();
  const { t, catName, money, jt, cityName } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [message, setMessage] = useState("");
  const [price, setPrice] = useState("");
  const [applied, setApplied] = useState(false);
  const [ack, setAck] = useState(false);
  const [reported, setReported] = useState(false);

  const isCraftsman = user && user !== false && user.role === "craftsman";

  useEffect(() => { api.get(`/jobs/${id}`).then((r) => setJob(r.data)); }, [id]);

  useEffect(() => {
    if (!isCraftsman) return;
    api.get("/applications/mine").then((r) => {
      if ((r.data || []).some((a) => a.job && a.job._id === id)) setApplied(true);
    }).catch(() => {});
    api.get("/reports/mine").then((r) => {
      if ((r.data || []).includes(id)) setReported(true);
    }).catch(() => {});
  }, [id, isCraftsman]);

  const apply = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/jobs/${id}/apply`, { message, price: price ? Number(price) : null });
      setApplied(true);
      toast.success(t("job.applied"));
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(t("credits.need"), { action: { label: t("credits.buy"), onClick: () => nav("/dashboard") } });
      } else {
        toast.error(apiError(err.response?.data?.detail) || err.message);
      }
    }
  };

  if (!job) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[#A89A90]">…</div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <span className="px-2.5 py-1 rounded-full bg-[#5C6646]/10 text-[#5C6646] font-semibold text-xs">{catName(job.category)}</span>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F] mt-4">{jt(job, "title")}</h1>
        <div className="flex items-center gap-5 mt-4 text-sm text-[#8C7A70] flex-wrap">
          <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{cityName(job.city)}</span>
          {job.budget != null && <span className="inline-flex items-center gap-1 font-semibold text-[#5A4B45]"><Wallet className="w-4 h-4" />{money(job.budget)}</span>}
          <span className="inline-flex items-center gap-1"><User className="w-4 h-4" />{job.customer_name}</span>
        </div>

        <div className="mt-8 rounded-2xl border border-[#EBDDD3] bg-white p-8">
          <h2 className="font-semibold text-[#2D231F] mb-3">{t("job.description")}</h2>
          <p className="text-[#5A4B45] leading-relaxed whitespace-pre-line">{jt(job, "description")}</p>
        </div>

        {job.images && job.images.length > 0 && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="job-images">
            {job.images.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noreferrer">
                <img src={url} alt="" className="w-full h-32 object-cover rounded-xl border border-[#EBDDD3] hover:opacity-90 transition-opacity" />
              </a>
            ))}
          </div>
        )}

        <TrustBadge trust={job.customer_trust} hint={isCraftsman && !applied} />

        {isCraftsman && applied && (
          <div className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-[#EBDDD3] bg-white px-6 py-4">
            <span className="text-sm text-[#8C7A70]">{t("report.desc")}</span>
            <ReportDialog jobId={job._id} reported={reported} onDone={() => setReported(true)} />
          </div>
        )}

        {isCraftsman && !applied && (
          <div className="mt-6 rounded-2xl border border-[#EBDDD3] bg-white p-8">
            <h2 className="font-display text-xl font-bold text-[#2D231F] mb-5">{t("job.apply")}</h2>
            {applied ? (
              <p className="text-[#5C6646] font-semibold">{t("job.applied")}</p>
            ) : (
              <form onSubmit={apply} className="space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#B27A1E] bg-[#E69625]/10 border border-[#E69625]/30 rounded-lg px-3 py-2">
                  <Coins className="w-4 h-4 shrink-0" /> {t("apply.cost")}
                </div>
                <div className="space-y-2">
                  <Label>{t("job.yourMessage")}</Label>
                  <Textarea data-testid="apply-message" required rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-lg" />
                  <p data-testid="guard-hint" className="text-xs text-[#8C7A70]">{t("guard.hint")}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t("job.yourPrice")}</Label>
                  <Input data-testid="apply-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="rounded-lg" />
                </div>
                <label className="flex items-start gap-2 text-xs text-[#5A4B45] cursor-pointer">
                  <input type="checkbox" data-testid="apply-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#C65338] shrink-0" />
                  <span>{t("apply.ack")}</span>
                </label>
                <Button data-testid="apply-submit" type="submit" disabled={!ack} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 px-8 font-semibold disabled:opacity-50">{t("job.submit")}</Button>
              </form>
            )}
          </div>
        )}

        {(!user || user === false) && (
          <div className="mt-6 rounded-2xl border border-[#EBDDD3] bg-white p-6 text-center">
            <p className="text-[#5A4B45] mb-3">{t("job.apply")}</p>
            <Button data-testid="login-to-apply" onClick={() => nav("/login")} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white">{t("nav.login")}</Button>
          </div>
        )}
      </motion.div>
    </div>
  );
}